import { AvatarV2 } from '@cimplico/components';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';
import { matchPath } from 'react-router-dom';
import { useWorkpaper } from '../../queries/use-workpaper';
import { parseTitle } from '../../utilities/formatting';
import { Menu } from '@headlessui/react';
import { motion } from 'framer-motion';
import WorkpaperQuickNavigateDropdown from './WorkpaperQuickNavigateDropdown';

function isValidUuid(value?: string): boolean {
    const expression = /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i;
    if (!value) {
        return false;
    }
    return expression.test(value);
}

type WorkpaperQuickNavigateProps = {
    isCollapsed?: boolean;
};

function WorkpaperQuickNavigate({ isCollapsed = false }: WorkpaperQuickNavigateProps): JSX.Element {
    const [workpaperId, setWorkpaperId] = useState<string | undefined>(undefined);
    const [collapsed, setCollapsed] = useState(isCollapsed);
    const path = '/:shortId/workpapers/:workpaperId/*';
    const match = matchPath({ path }, document.location.pathname);

    useEffect(() => {
        const id = match?.params.workpaperId;
        if (isValidUuid(id)) {
            setWorkpaperId(id);
        }
    }, [match?.params]);

    useEffect(() => {
        setCollapsed(isCollapsed);
    }, [isCollapsed]);

    const workpaperQuery = useWorkpaper(workpaperId);

    if (workpaperQuery.isLoading || !workpaperQuery.isSuccess) {
        return <></>;
    }

    return (
        <Menu>
            <motion.div initial={false} animate={{ width: collapsed ? 'auto' : 207 }} transition={{ duration: 0.5 }}>
                <Menu.Button
                    className={`${collapsed ? 'w-auto' : 'w-[207px]'} focus:outline-none focus:ring-0`}
                    disabled={!workpaperQuery.data?.workpaper || !workpaperId}
                >
                    <div className="flex items-center gap-2 justify-between">
                        <AvatarV2
                            alt={`${parseTitle(workpaperQuery.data?.workpaper.entity.name || '')}`}
                            size="sm"
                            rounded={false}
                            color={workpaperQuery.data?.workpaper.entity.avatarHexColour}
                        />
                        {!collapsed && (
                            <div className="flex flex-col text-xs text-left w-[150px] overflow-hidden">
                                <p className="text-gray-800 dark:text-gray-50 font-medium">{workpaperQuery.data?.workpaper.entity.name}</p>
                                <p className="text-gray-600 dark:text-gray-400">{workpaperQuery.data?.workpaper.name}</p>
                            </div>
                        )}
                        {!collapsed && <ChevronDownIcon className="h-5 w-5 shrink-0 text-gray-800 dark:text-gray-50" />}
                    </div>
                </Menu.Button>
            </motion.div>
            <Menu.Items
                className={`${
                    collapsed ? 'translate-x-[118px]' : 'translate-x-7'
                } p-2 rounded-lg max-h-[450px] min-w-[250px] overflow-y-auto focus:outline-none focus:ring-0 absolute top-[40px] z-40 border border-gray-300 dark:border-white/[0.07] bg-white dark:bg-gray-800 grid text-xs font-normal text-gray-700 dark:text-gray-200`}
            >
                <WorkpaperQuickNavigateDropdown
                    collapsed={collapsed}
                    entityId={workpaperQuery.data.workpaper.entity.id}
                    workpaper={workpaperQuery.data.workpaper}
                />
            </Menu.Items>
        </Menu>
    );
}

export default WorkpaperQuickNavigate;
